/**
 * Usage, like Claude Code (TMMAPP-0142 item 5; MM-D-140, never tokens): the session's cost, the last
 * answer's cost, a context meter with its tooltip, and the AI balance (`GET /v1/me/ai-wallet`) with
 * Top up. Amounts are `MoneyText` in the wallet's currency. The context meter is a `meter` with its
 * value, and its tooltip describes it, so both are read out (H6).
 *
 * On a session someone else pays for (TMMAPI-0184 U3: an anchored session's `paidBy` isn't the
 * person), the person's own balance and Top up aren't shown: that wallet doesn't pay here.
 */
import { useState } from 'react';
import { Box, Button, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AiWallet } from '@/api/aiWallet';
import type { AssistantSession } from '@/api/assistant';
import { MoneyText } from '@/components/MoneyText';
import { ProgressTrack } from '@/components/ProgressTrack';
import { ChargeOutcomeNotice } from '@/features/aiWallet/ChargeOutcomeNotice';
import { TopUpDialog } from '@/features/aiWallet/TopUpDialog';
import {
  chargeInFlight,
  topUpStaysOpen,
  useAiWalletCharge,
} from '@/features/aiWallet/useAiWalletCharge';
import { formatPercent } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';

interface UsageBarProps {
  session: AssistantSession;
  wallet: AiWallet | null;
  onWallet: (wallet: AiWallet) => void;
  /** The person's own AI balance and Top up (where they pay); default shown. */
  showBalance?: boolean | undefined;
}

export function UsageBar({ session, wallet, onWallet, showBalance = true }: UsageBarProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const [topUpOpen, setTopUpOpen] = useState(false);
  const charge = useAiWalletCharge({
    wallet,
    onWallet: (next) => {
      if (next !== null) onWallet(next);
    },
  });
  const currency = session.currency ?? wallet?.currency ?? null;
  const label = { fontSize: 12, color: tokens.text2 } as const;
  const percent = formatPercent(session.contextFillPercent / 100, locale);

  return (
    <Box data-usage-bar="">
      <Stack
        direction="row"
        role="group"
        aria-label={t('assistant.usage.label')}
        sx={{ alignItems: 'center', columnGap: 2.5, rowGap: 1, flexWrap: 'wrap' }}
      >
        <Stack direction="row" sx={{ alignItems: 'baseline', gap: 0.75 }} data-usage-session="">
          <Typography component="span" sx={label}>
            {t('assistant.usage.session')}
          </Typography>
          <MoneyText
            minor={currency === null ? null : session.chargedMinor}
            currency={currency}
            variant="body2"
          />
        </Stack>
        <Stack direction="row" sx={{ alignItems: 'baseline', gap: 0.75 }} data-usage-last="">
          <Typography component="span" sx={label}>
            {t('assistant.usage.lastAnswer')}
          </Typography>
          <MoneyText
            minor={currency === null ? null : session.lastTurnChargedMinor}
            currency={currency}
            variant="body2"
          />
        </Stack>
        <Tooltip title={t('assistant.usage.contextTooltip')} describeChild>
          <Stack
            direction="row"
            tabIndex={0}
            role="meter"
            aria-label={t('assistant.usage.context')}
            aria-valuenow={session.contextFillPercent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuetext={t('assistant.usage.contextFull', { percent })}
            sx={{ alignItems: 'center', gap: 0.75, borderRadius: 1 }}
            data-usage-context=""
          >
            <Typography component="span" sx={label}>
              {t('assistant.usage.context')}
            </Typography>
            {/* The shared filled track (TMMAPP-0156 item 4); the meter around it is named. */}
            <Box sx={{ width: 64 }}>
              <ProgressTrack value={session.contextFillPercent} />
            </Box>
            <Typography component="span" sx={{ fontSize: 12, color: tokens.text }}>
              {t('assistant.usage.contextFull', { percent })}
            </Typography>
          </Stack>
        </Tooltip>
        {wallet !== null && showBalance && (
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75 }} data-usage-balance="">
            <Typography component="span" sx={label}>
              {t('assistant.usage.balance')}
            </Typography>
            <MoneyText minor={wallet.availableMinor} currency={wallet.currency} variant="body2" />
            {wallet.firstPurchaseDone && (
              <Button
                size="small"
                disabled={chargeInFlight(charge.outcome)}
                onClick={() => {
                  charge.reset();
                  setTopUpOpen(true);
                }}
              >
                {t('aiWallet.topUp')}
              </Button>
            )}
          </Stack>
        )}
      </Stack>
      {(!topUpOpen || !topUpStaysOpen(charge.outcome)) &&
        charge.outcome.kind !== 'idle' &&
        charge.outcome.kind !== 'busy' && (
          <Box sx={{ mt: 1 }}>
            <ChargeOutcomeNotice outcome={charge.outcome} onRefresh={() => void charge.refresh()} />
          </Box>
        )}
      {topUpOpen && topUpStaysOpen(charge.outcome) && wallet !== null && (
        <TopUpDialog
          currency={wallet.currency}
          minimumMinor={wallet.rechargeAmountMinor}
          outcome={charge.outcome}
          onTopUp={(amountMinor) => void charge.topUp(amountMinor)}
          onRefresh={() => void charge.refresh()}
          onClose={() => {
            setTopUpOpen(false);
            charge.reset();
          }}
        />
      )}
    </Box>
  );
}
